import { useCallback, useState } from 'react'
import { cn } from '#app/utils/misc'
import {
	formatClock,
	useFrameLoop,
	useReducedMotionSafe,
	useSceneClock,
} from '#app/utils/site/scene-clock'
import {
	APPROVE_CLOCK,
	APPROVE_FINAL_PHASE,
	APPROVE_KEYFRAMES,
	APPROVE_LOOP_MS,
	APPROVE_SCENE,
	type AvatarTone,
	type TapTarget,
	approveClockSecondsAt,
	approveStateAt,
} from '#app/utils/site/scenes/approve-phone'
import { Wordmark } from '../../Wordmark'
import { CheckIcon } from '../../primitives'
import type { SceneProps } from './types'

/**
 * Landlord scene "Get Back To Them In 2 Minutes" ("approve-phone"): a
 * notification on a locked phone, a finger that opens it, picks Sarah
 * Johnson from four verified applications, watches four checks pop and
 * approves her; the phone then slides aside for the "Approved in 0:45"
 * popout. Twelve and a half seconds, looping while in view.
 *
 * The timeline, the demo data and the phase-to-state mapping live in
 * app/utils/site/scenes/approve-phone.ts; this file only draws a state.
 */
export function ApprovePhoneScene({ active }: SceneProps) {
	const { phase } = useSceneClock({
		active,
		keyframes: APPROVE_KEYFRAMES,
		loopMs: APPROVE_LOOP_MS,
		finalPhase: APPROVE_FINAL_PHASE,
	})
	const seconds = useApproveClock(active)
	const state = approveStateAt(phase)
	const [targets, setTargets] = useState<TapPoints>({})
	const measureScreen = useCallback((screen: HTMLDivElement | null) => {
		if (!screen) return
		const observer = new ResizeObserver(() => {
			const next = measureTapTargets(screen)
			setTargets((prev) => (sameTargets(prev, next) ? prev : next))
		})
		observer.observe(screen)
		// A late font swap can re-wrap a name and move the rows below it
		// without resizing the screen, so the targets are watched too.
		for (const el of screen.querySelectorAll('[data-tap-target]')) {
			observer.observe(el)
		}
		return () => observer.disconnect()
	}, [])
	const tapPoint = targets[state.tap.target]
	const copy = APPROVE_SCENE
	const sarah = copy.applicants[0]

	return (
		<div
			data-scene="approve-phone"
			aria-hidden="true"
			className="tt-scene-approve-phone -my-2.5 w-full md:-my-6"
		>
			<div
				ref={fitScaledStage}
				data-moved={state.moved || undefined}
				className="tt-scene-approve-phone-stage relative flex h-[560px] items-center justify-center leading-[1.2]"
			>
				<div className="tt-scene-approve-phone-phone relative z-[2] aspect-[9/19.5] w-[252px] overflow-hidden rounded-[40px] bg-[#1E1D1C] p-2 shadow-[0_34px_60px_-22px_rgba(30,28,27,.45)]">
					<div
						ref={measureScreen}
						className="tt-scene-approve-phone-screen absolute inset-2 isolate overflow-hidden rounded-[32px] bg-white text-[11px]"
					>
						<div
							data-unlocked={state.unlocked || undefined}
							className="tt-scene-approve-phone-lock absolute inset-0 z-[3] bg-[linear-gradient(165deg,#EEE8F3,#E9EEE3)] pt-[62px] text-center"
						>
							<div className="text-[12.5px] text-tt-body">{copy.lock.date}</div>
							<div className="text-[62px] font-semibold leading-[1.05] tracking-[-.035em] text-tt-ink">
								{copy.lock.time}
							</div>
							<div
								data-state={state.notification}
								data-tap-target="notify"
								className="tt-scene-approve-phone-ntf mx-3 mt-[26px] flex gap-2.5 rounded-[18px] bg-white/90 px-3 py-[11px] text-left shadow-tt-soft"
							>
								<span className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-[9px] bg-white text-tt-ink shadow-tt-soft">
									<Wordmark title={null} className="h-auto w-7" />
								</span>
								<div className="min-w-0">
									<b className="block text-[12.5px] font-semibold">
										{copy.notification.title}
									</b>
									<small className="mt-px block text-[11.5px] leading-[1.35] text-tt-body">
										{copy.notification.body}
									</small>
								</div>
								<time className="ml-auto flex-none text-[10.5px] text-tt-muted">
									{copy.notification.when}
								</time>
							</div>
						</div>

						<div className="absolute inset-x-0 top-0 z-[2] flex items-center justify-between gap-2 bg-white px-4 pb-2 pt-[46px]">
							<Wordmark title={null} className="h-3 max-[520px]:h-[11px]" />
							<small className="whitespace-nowrap text-[10.5px] text-tt-muted max-[520px]:text-[9px]">
								{copy.bar.label}{' '}
								<b className="tt-tabular font-bold text-tt-ink">
									{formatClock(seconds)}
								</b>
							</small>
						</div>

						<div
							data-state={state.list}
							className="tt-scene-approve-phone-list absolute inset-x-0 bottom-0 top-[72px] flex flex-col px-3.5 pb-3.5 pt-1.5"
						>
							<h6 className="text-[17px] font-semibold tracking-[-.02em]">
								{copy.address}
							</h6>
							<p className="mt-0.5 text-[11px] text-tt-muted">
								{copy.list.subtitle}
							</p>
							{copy.applicants.map((applicant, i) => (
								<div
									key={applicant.name}
									data-tap-target={`row${i}`}
									data-tap-x="0.6"
									data-hover={state.hoveredRow === i || undefined}
									data-selected={(i === 0 && state.selectedRow) || undefined}
									className="tt-scene-approve-phone-row mt-2 flex items-center gap-2.5 rounded-[14px] border border-tt-line-soft p-2.5"
								>
									<AvatarGroup avatars={applicant.avatars} size="sm" />
									<div className="min-w-0 flex-1">
										<b className="block text-[13px] font-semibold">
											{applicant.name}
										</b>
									</div>
									<span className="text-[16px] text-tt-muted">›</span>
								</div>
							))}
						</div>

						<div
							data-shown={state.detail || undefined}
							className="tt-scene-approve-phone-detail absolute inset-x-0 bottom-0 top-[72px] flex flex-col px-3.5 pb-3.5 pt-1.5"
						>
							<span className="text-[11px] text-tt-muted">
								‹ {copy.list.back}
							</span>
							<div className="mt-1.5 flex items-center gap-2.5">
								<AvatarGroup avatars={sarah.avatars} size="lg" />
								<div className="min-w-0">
									<b className="block text-[16px] font-semibold">
										{sarah.name}
									</b>
									<small className="block text-[10.5px] text-tt-muted">
										{copy.address}
									</small>
								</div>
							</div>
							<div className="mt-3.5 overflow-hidden rounded-2xl border border-tt-line-soft">
								{copy.verifications.map((row, i) => (
									<div
										key={row.label}
										data-on={i < state.checks || undefined}
										className="tt-scene-approve-phone-vr flex items-center gap-2.5 border-t border-tt-line-soft px-[13px] py-3.5 first:border-t-0"
									>
										<span className="tt-scene-approve-phone-check flex-none">
											<CheckIcon className="h-[18px] w-[18px]" />
										</span>
										{'value' in row ? (
											<span className="min-w-0 flex-1 text-[11.5px] font-medium text-tt-muted">
												{row.label}
												<b className="tt-scene-approve-phone-figure tt-tabular mt-0.5 block text-[15px] font-semibold tracking-[-.01em] text-tt-ink">
													{row.value}
													{'unit' in row ? (
														<small className="ml-0.5 text-[11px] font-medium text-tt-muted">
															{row.unit}
														</small>
													) : null}
												</b>
											</span>
										) : (
											<span className="min-w-0 flex-1 text-[13px] font-semibold text-tt-ink">
												{row.label}
											</span>
										)}
									</div>
								))}
							</div>
							<div
								data-tap-target="approve"
								data-state={state.approve}
								className="tt-scene-approve-phone-btn mt-auto flex items-center justify-center rounded-3xl bg-tt-ink p-3 text-[13px] font-semibold text-white"
							>
								{state.approve === 'approved'
									? copy.approve.done
									: copy.approve.idle}
							</div>
						</div>

						<i
							data-visible={state.tap.visible || undefined}
							data-hit={state.tap.hit || undefined}
							style={
								tapPoint
									? { transform: `translate(${tapPoint.x}px, ${tapPoint.y}px)` }
									: undefined
							}
							className="tt-scene-approve-phone-tap pointer-events-none absolute left-0 top-0 z-[8] -ml-[17px] -mt-[17px] block h-[34px] w-[34px]"
						>
							<span className="tt-scene-approve-phone-tap-ring block h-full w-full rounded-full border-2 border-[rgba(50,48,47,.32)] bg-[rgba(50,48,47,.16)]" />
						</i>
					</div>
				</div>

				<div className="tt-scene-approve-phone-pop absolute left-1/2 top-1/2 z-[1] ml-2 w-[252px] rounded-[22px] bg-white p-[18px] shadow-tt-card">
					<div className="flex items-center gap-2.5">
						<CheckIcon className="h-[26px] w-[26px]" />
						<div className="min-w-0">
							<b className="block text-[15px] font-semibold">
								{copy.popout.title}
							</b>
							<small className="block text-[12px] text-tt-muted">
								{copy.address}
							</small>
						</div>
					</div>
					<div className="mt-3.5 flex items-baseline justify-between border-t border-tt-line-soft pt-3 text-[12px] text-tt-muted">
						<span>{copy.popout.approvedIn}</span>
						<b className="tt-scene-approve-phone-time text-[34px] text-tt-ink">
							{copy.popout.time}
						</b>
					</div>
					<div className="mt-3.5 flex items-center justify-center gap-[7px] whitespace-nowrap rounded-full bg-tt-ink px-2.5 py-[11px] text-[12.5px] font-semibold text-white">
						<DocumentIcon />
						{copy.popout.next}
					</div>
				</div>
			</div>
		</div>
	)
}

/* ---------- pieces ---------- */

const AVATAR_TONE: Record<AvatarTone, string> = {
	sage: 'bg-tt-sage',
	lav: 'bg-tt-lav',
	pink: 'bg-[#F9D7E6]',
}

function AvatarGroup({
	avatars,
	size,
}: {
	avatars: readonly { initials: string; tone: AvatarTone }[]
	size: 'sm' | 'lg'
}) {
	return (
		<span className={cn('flex flex-none', size === 'sm' && 'w-[52px]')}>
			{avatars.map((avatar, i) => (
				<span
					key={avatar.initials}
					className={cn(
						'flex flex-none items-center justify-center rounded-full font-bold text-tt-ink',
						size === 'sm'
							? 'h-7 w-7 text-[9.5px]'
							: 'h-[38px] w-[38px] text-[12px]',
						i > 0 && '-ml-1.5 border-2 border-white',
						AVATAR_TONE[avatar.tone],
					)}
				>
					{avatar.initials}
				</span>
			))}
		</span>
	)
}

function DocumentIcon() {
	return (
		<svg
			className="h-[15px] w-[15px]"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={1.8}
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<path d="M6 3.2h7.5L18 7.8V20.8H6z" />
			<path d="M13.5 3.2v4.6H18" />
			<path d="M9 12.5h6M9 16h6" />
		</svg>
	)
}

/* ---------- the stopwatch ---------- */

/**
 * Whole seconds on the "Time to Approve" clock. Runs on the same animation
 * frames as the scene clock (both start on the first frame after `active`),
 * re-rendering only when the second changes, and takes no frames while
 * the tile is out of view. Reduced motion shows 0:45.
 */
function useApproveClock(active: boolean) {
	const reduced = useReducedMotionSafe()
	const [seconds, setSeconds] = useState(0)
	const [wasActive, setWasActive] = useState(active)
	if (active !== wasActive) {
		setWasActive(active)
		if (!active && seconds !== 0) setSeconds(0)
	}
	useFrameLoop(active && !reduced, (elapsed) => {
		const next = approveClockSecondsAt(elapsed)
		if (next !== seconds) setSeconds(next)
	})
	return reduced ? APPROVE_CLOCK.targetSeconds : seconds
}

/* ---------- measuring (ref callbacks, no effects) ---------- */

type TapPoints = Partial<Record<TapTarget, { x: number; y: number }>>

/** Position of `el` inside `root` from layout offsets, so transforms (the
 * detail slide-in, the phone-size scale) never skew the measurement. */
function offsetWithin(el: HTMLElement, root: HTMLElement) {
	let x = 0
	let y = 0
	let node: HTMLElement | null = el
	while (node && node !== root) {
		x += node.offsetLeft
		y += node.offsetTop
		node = node.offsetParent as HTMLElement | null
	}
	return { x, y }
}

/** Where the finger lands for each `data-tap-target` inside the screen. */
function measureTapTargets(screen: HTMLElement): TapPoints {
	const points: TapPoints = {}
	for (const el of screen.querySelectorAll<HTMLElement>('[data-tap-target]')) {
		const target = el.dataset.tapTarget as TapTarget
		const fx = Number(el.dataset.tapX ?? 0.5)
		const { x, y } = offsetWithin(el, screen)
		points[target] = {
			x: x + el.offsetWidth * fx,
			y: y + el.offsetHeight / 2,
		}
	}
	return points
}

function sameTargets(a: TapPoints, b: TapPoints) {
	const keys = Object.keys(b) as TapTarget[]
	if (keys.length !== Object.keys(a).length) return false
	return keys.every((key) => a[key]?.x === b[key]?.x && a[key]?.y === b[key]?.y)
}

/** The row composition, in CSS pixels (see the stylesheet): the phone slid
 * left of centre, the popout 8px right of it, and 8px of air each side. */
const PHONE_WIDTH = 252
const PHONE_SHIFT = 128
const POPOUT_WIDTH = 252
const POPOUT_GAP = 8
const ROW_AIR = 8
const ROW_COMPOSITION_WIDTH =
	PHONE_WIDTH / 2 + PHONE_SHIFT + POPOUT_GAP + POPOUT_WIDTH + 2 * ROW_AIR

/**
 * The stage is drawn at `--tt-ax-zoom` (laid out at 100% / zoom, then
 * scaled, like the reference's `zoom`). Reads the stylesheet's value
 * (`--tt-ax-zoom-base`), shrinks the row layout further whenever the tile
 * is narrower than the composition, writes the zoom back on the stage and
 * gives the wrapper the scaled height so the tile hugs what is visible.
 * Reads first, then writes; a zoom change resizes the stage, so the
 * observer runs once more and settles on the new height.
 */
function fitScaledStage(stage: HTMLDivElement | null) {
	if (!stage) return
	const wrapper = stage.parentElement
	if (!wrapper) return
	const fit = () => {
		const style = getComputedStyle(stage)
		const base =
			Number.parseFloat(style.getPropertyValue('--tt-ax-zoom-base')) || 1
		const column = style.flexDirection === 'column'
		const tileWidth = wrapper.parentElement?.clientWidth ?? wrapper.clientWidth
		const room = column ? 1 : Math.min(1, tileWidth / ROW_COMPOSITION_WIDTH)
		const zoom = Math.round(Math.min(base, room) * 1000) / 1000
		const height = stage.offsetHeight
		if (zoom === base) stage.style.removeProperty('--tt-ax-zoom')
		else stage.style.setProperty('--tt-ax-zoom', String(zoom))
		wrapper.style.height = zoom === 1 ? '' : `${Math.round(height * zoom)}px`
	}
	const observer = new ResizeObserver(fit)
	observer.observe(stage)
	return () => observer.disconnect()
}
